import { useState, FormEvent, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const FORM_URL = "https://readdy.ai/api/form/d6vtmfl1l1ubpb129kig";
const MESSENGER_URL =
  "https://m.me/jglennverano?text=Hi%20I%20want%20to%20ask%20about%20my%20car%20loan%20options";

const gradientStyle = {
  background: "linear-gradient(135deg, #4F7CFF 0%, #7B8CFF 100%)",
};
const gradientHover = {
  background: "linear-gradient(135deg, #3D6AE8 0%, #6B7CE8 100%)",
};

type Status = "idle" | "submitting" | "success";

const inputClass =
  "w-full rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 transition";
const inputStyle = {
  backgroundColor: "#F8FAFC",
  border: "1px solid #E2E8F0",
  color: "#1E293B",
};
const labelClass = "mb-1.5 block text-xs font-semibold";
const labelStyle = { color: "#64748B" };

export default function IntakeFormPage() {
  const [status, setStatus] = useState<Status>("idle");
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstInputRef.current?.focus();
    window.scrollTo({ top: 0 });
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    const data = new FormData(e.currentTarget);
    const params = new URLSearchParams();
    data.forEach((value, key) => params.append(key, value.toString()));
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus("success");
        (e.target as HTMLFormElement).reset();
      } else {
        setStatus("idle");
      }
    } catch {
      setStatus("idle");
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F8FAFC" }}>

      {/* Minimal top bar */}
      <header
        className="w-full border-b"
        style={{ backgroundColor: "#FFFFFF", borderColor: "#E2E8F0" }}
      >
        <div className="mx-auto flex h-20 max-w-5xl items-center justify-between px-6">
          <Link to="/" className="flex items-center">
            <img
              src="https://public.readdy.ai/ai/img_res/edited_24ec1a8935e927938c764a237761f43e_29c252ee.jpg"
              alt="AutoManagement PH"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium transition cursor-pointer whitespace-nowrap"
            style={{ color: "#64748B" }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLElement).style.color = "#1E293B")
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLElement).style.color = "#64748B")
            }
          >
            <i className="ri-arrow-left-line text-sm" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Page body */}
      <main className="mx-auto max-w-2xl px-6 py-14 lg:py-20">

        {/* Trust pills row */}
        <div className="mb-8 flex flex-wrap justify-center gap-2.5">
          {["Legal & Compliant", "PH-Based Team", "Confidential"].map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium"
              style={{ backgroundColor: "#EBF0FF", color: "#4F7CFF" }}
            >
              <i className="ri-checkbox-circle-line text-xs" />
              {tag}
            </span>
          ))}
        </div>

        {/* Heading */}
        <div className="mb-10 text-center">
          <h1
            className="text-3xl font-bold tracking-tight sm:text-4xl"
            style={{ color: "#1E293B" }}
          >
            Tell us about your vehicle
          </h1>
          <p className="mt-3 text-base leading-7" style={{ color: "#475569" }}>
            Complete this short form so our team can review your situation.
          </p>
          <p className="mt-1.5 text-sm font-medium" style={{ color: "#4F7CFF" }}>
            <i className="ri-time-line mr-1" />
            Takes less than 1 minute
          </p>
        </div>

        {/* Form card */}
        <div
          className="rounded-2xl p-8 lg:p-10"
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #E2E8F0",
          }}
        >
          {status === "success" ? (
            <div className="flex flex-col items-center py-12 text-center gap-5">
              <div
                className="flex h-16 w-16 items-center justify-center rounded-full"
                style={{ backgroundColor: "#F0FDF4" }}
              >
                <i
                  className="ri-checkbox-circle-line text-4xl"
                  style={{ color: "#22C55E" }}
                />
              </div>
              <h2 className="text-xl font-bold" style={{ color: "#1E293B" }}>
                We received your form!
              </h2>
              <p className="max-w-sm text-sm leading-7" style={{ color: "#475569" }}>
                Our team will review your situation and reach out within 1
                business day. Keep an eye on your phone and email.
              </p>
              <div className="flex flex-wrap justify-center gap-3 mt-2">
                <button
                  onClick={() => setStatus("idle")}
                  className="whitespace-nowrap rounded-lg px-6 py-2.5 text-sm font-semibold transition cursor-pointer"
                  style={{ backgroundColor: "#EBF0FF", color: "#4F7CFF" }}
                >
                  Submit another
                </button>
                <Link
                  to="/"
                  className="whitespace-nowrap rounded-lg px-6 py-2.5 text-sm font-semibold text-white transition cursor-pointer"
                  style={gradientStyle}
                >
                  Back to Home
                </Link>
              </div>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              {/* Personal info */}
              <div>
                <p
                  className="mb-4 text-xs font-bold uppercase tracking-widest"
                  style={{ color: "#4F7CFF" }}
                >
                  Your Details
                </p>
                <div className="space-y-4">
                  <div>
                    <label className={labelClass} style={labelStyle}>
                      Full Name <span style={{ color: "#4F7CFF" }}>*</span>
                    </label>
                    <input
                      ref={firstInputRef}
                      type="text"
                      name="name"
                      required
                      placeholder="e.g. Juan dela Cruz"
                      className={inputClass}
                      style={inputStyle}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelClass} style={labelStyle}>
                        Phone / Viber <span style={{ color: "#4F7CFF" }}>*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="+63 9XX XXX XXXX"
                        className={inputClass}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label className={labelClass} style={labelStyle}>
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        placeholder="juan@email.com"
                        className={inputClass}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px" style={{ backgroundColor: "#F1F5F9" }} />

              {/* Vehicle info */}
              <div>
                <p
                  className="mb-4 text-xs font-bold uppercase tracking-widest"
                  style={{ color: "#4F7CFF" }}
                >
                  Vehicle Info
                </p>
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <div>
                      <label className={labelClass} style={labelStyle}>
                        Year <span style={{ color: "#4F7CFF" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="vehicle_year"
                        required
                        placeholder="e.g. 2020"
                        maxLength={4}
                        className={inputClass}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label className={labelClass} style={labelStyle}>
                        Make <span style={{ color: "#4F7CFF" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="vehicle_make"
                        required
                        placeholder="e.g. Toyota"
                        className={inputClass}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label className={labelClass} style={labelStyle}>
                        Model <span style={{ color: "#4F7CFF" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="vehicle_model"
                        required
                        placeholder="e.g. Vios"
                        className={inputClass}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px" style={{ backgroundColor: "#F1F5F9" }} />

              {/* Loan info */}
              <div>
                <p
                  className="mb-4 text-xs font-bold uppercase tracking-widest"
                  style={{ color: "#4F7CFF" }}
                >
                  Loan Details
                </p>
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={labelClass} style={labelStyle}>
                        Monthly Payment <span style={{ color: "#4F7CFF" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="monthly_payment"
                        required
                        placeholder="Enter monthly payment in PHP"
                        className={inputClass}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label className={labelClass} style={labelStyle}>
                        Pay-off Amount <span style={{ color: "#4F7CFF" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="payoff_amount"
                        required
                        placeholder="Enter pay-off amount in PHP"
                        className={inputClass}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass} style={labelStyle}>
                      Are payments current? <span style={{ color: "#4F7CFF" }}>*</span>
                    </label>
                    <div className="mt-1 flex gap-3">
                      {["Yes", "No"].map((opt) => (
                        <label
                          key={opt}
                          className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition"
                          style={{ border: "1px solid #E2E8F0", backgroundColor: "#F8FAFC", color: "#475569" }}
                        >
                          <input
                            type="radio"
                            name="payments_current"
                            value={opt}
                            required
                            className="accent-[#4F7CFF]"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full whitespace-nowrap rounded-xl py-4 text-base font-bold text-white transition disabled:opacity-60 cursor-pointer"
                  style={gradientStyle}
                  onMouseEnter={(e) =>
                    Object.assign((e.currentTarget as HTMLElement).style, gradientHover)
                  }
                  onMouseLeave={(e) =>
                    Object.assign((e.currentTarget as HTMLElement).style, gradientStyle)
                  }
                >
                  {status === "submitting" ? "Submitting..." : "Submit for Free Review"}
                </button>

                <p
                  className="mt-3 text-center text-xs"
                  style={{ color: "#94A3B8" }}
                >
                  Confidential. No pressure. PH-based team.
                </p>
              </div>
            </form>
          )}
        </div>

        {/* Messenger fallback */}
        <div
          className="mt-6 rounded-xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ backgroundColor: "#FFFFFF", border: "1px solid #E2E8F0" }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
              style={{ backgroundColor: "#E8F4FF" }}
            >
              <i className="ri-messenger-line text-lg" style={{ color: "#0084FF" }} />
            </div>
            <p className="text-sm" style={{ color: "#475569" }}>
              Prefer to chat instead?
            </p>
          </div>
          <a
            href={MESSENGER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap rounded-lg px-5 py-2 text-sm font-semibold transition cursor-pointer"
            style={{ backgroundColor: "#0084FF", color: "#FFFFFF" }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLElement).style.backgroundColor = "#006FD6")
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLElement).style.backgroundColor = "#0084FF")
            }
          >
            Message us on Messenger
          </a>
        </div>

      </main>
    </div>
  );
}